import React, { useState, useCallback, useMemo } from "react"
import PropTypes from "prop-types"
import { Row, Col, Flex, Card, Button, Divider, Tooltip, message } from "antd"
import moment from "moment"
import Hash from "../../../../../assets/icons/Hash.svg"
import Calendar from "../../../../../assets/icons/calendar.svg"
import TimeIcon from "../../../../../assets/icons/time.svg"
import GuestsIcon from "../../../../../assets/icons/guest.svg"
import DurationIcon from "../../../../../assets/icons/duration.svg"
import AddressIcon from "../../../../../assets/icons/Address.svg"
import { DirectionIcon } from "ui/Icon/DSIcon/Direction"
import styles from "../../index.module.css"
import { GoogleMap, Marker, Circle, useJsApiLoader } from "@react-google-maps/api"
import { API_OPTIONS, DEFAULT_ZOOM, MAP_OPTIONS } from "constants/map"
import { useDispatch, useSelector } from "react-redux"
import { getCancelReasons } from "store/bookings"
import tripImage from "assets/imagesV2/favicon_men.png"
import ZoomButtons from "components/map/ZoomButtons"
import { MapPin } from "ui/Icon/DSIcon/MapPin"
import Icon from "components/common/Icon"
import { CopyIcon } from "ui/Icon/DSIcon/CopyIcon"
import CancelBookingModal from "pagesV2/pro/Bookings/BookingActions/CancelBookingModal"

const BookingDetailsCard = React.memo(({ booking, trip, meetingInstructions }) => {
  const { uuid, booked_date, start_time, end_time } = booking || {}
  const { address, available_hours_range, latitude, longitude } = trip || {}
  const { isLoaded } = useJsApiLoader(API_OPTIONS)
  const [map, setMap] = useState(null)
  const [copied, setCopied] = useState(false)
  const [cancelModalOpen, setCancelModalOpen] = useState(false)
  const zoom = map?.zoom || DEFAULT_ZOOM
  const dispatch = useDispatch()
  const { adventure_mode } = useSelector((store) => store.guideProfile?.guide?.data || {})
  const reasons = useSelector((state) => state.bookings.cancelReasons || [])
  const themeColor = adventure_mode == "sportsman" ? "#1d72f4" : "#d92c9e"

  const onLoad = useCallback((mapInstance) => {
    setMap(mapInstance)
  }, [])

  const onUnmount = useCallback(() => {
    setMap(null)
  }, [])

  const handleCopyUuid = useCallback(() => {
    if (uuid) {
      navigator.clipboard
        .writeText(uuid)
        .then(() => {
          setCopied(true)
          setTimeout(() => setCopied(false), 2000) // Reset after 2 seconds
        })
        .catch((err) => {
          console.error("Failed to copy UUID:", err)
        })
    }
  }, [uuid])

  const handleCancelClick = () => {
    dispatch(getCancelReasons({ booking_id: booking?.id }))
    setCancelModalOpen(true)
  }

  const handleGetDirections = () => {
    let mapsUrl = ""

    if (latitude && longitude) {
      // Use coordinates if available
      mapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${latitude},${longitude}`
    } else if (address) {
      // Use address as fallback
      const encodedAddress = encodeURIComponent(address)
      mapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${encodedAddress}`
    }

    if (mapsUrl) {
      window.open(mapsUrl, "_blank")
    } else {
      message.error("Location information not available")
    }
  }

  const cancelBookingData = useMemo(() => {
    return {
      id: booking?.id,
      image: booking?.trip_image?.[0]?.placeholder_url || tripImage,
      title: booking?.trip_title,
      booking_id: booking?.booking_id,
      booked_on: booking?.create_at ? moment(booking?.create_at).format("MM/DD/YYYY, hh:mm A") : "",
      uuid: booking?.uuid
    }
  }, [booking])

  const rawLat = latitude
  const rawLng = longitude

  const latLng = rawLat && rawLng ? { lat: Number(rawLat), lng: Number(rawLng) } : null

  const radiusInMeters = trip?.location?.radius ? trip.location.radius * 1609.34 : 0

  return (
    <Card className={styles.mainCard} title="Booking Details">
      <Row gutter={[24, 24]} className="mb-s">
        <Col lg={6}>
          <Flex align="center" gap={8}>
            <img src={Hash} alt="Booking Id" />
            <span className="color-text-secondary-dark">Booking Id:</span>
          </Flex>
        </Col>
        <Col lg={18}>
          {uuid}
          <Tooltip title={copied ? "Copied!" : "Copy to clipboard"} open={copied || undefined}>
            <span
              className={styles.copyIcon}
              onClick={handleCopyUuid}
              role="button"
              tabIndex={0}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleCopyUuid();
                }
              }}
              aria-label="Copy Booking Id"
            >
              <CopyIcon />
            </span>
          </Tooltip>
        </Col>
      </Row>
      <Row gutter={[24, 24]} className="mb-s">
        <Col lg={6}>
          <Flex align="center" gap={8}>
            <img src={Calendar} alt="Date" />
            <span className="color-text-secondary-dark">Date:</span>
          </Flex>
        </Col>
        <Col lg={18}>{booked_date ? moment(booked_date).format("MM/DD/YYYY") : "-"}</Col>
      </Row>
      <Row gutter={[24, 24]} className="mb-s">
        <Col lg={6}>
          <Flex align="center" gap={8}>
            <img src={TimeIcon} alt="Time" />
            <span className="color-text-secondary-dark">Time:</span>
          </Flex>
        </Col>
        <Col lg={18}>{start_time && end_time ? `${start_time}-${end_time}` : "-"}</Col>
      </Row>
      <Row gutter={[24, 24]} className="mb-s">
        <Col lg={6}>
          <Flex align="center" gap={8}>
            <img src={DurationIcon} alt="Time" />
            <span className="color-text-secondary-dark">Duration:</span>
          </Flex>
        </Col>
        <Col lg={18}>{available_hours_range}</Col>
      </Row>
      <Row gutter={[24, 24]} className="mb-s">
        <Col lg={6}>
          <Flex align="center" gap={8}>
            <img src={GuestsIcon} alt="Guests" />
            <span className="color-text-secondary-dark">Guests:</span>
          </Flex>
        </Col>
        <Col lg={18}>{`${booking.adult_count} adults, ${booking.child_count} child`}</Col>
      </Row>
      <Row gutter={[24, 24]} className="mb-s">
        <Col lg={6}>
          <Flex align="center" gap={8}>
            <img src={AddressIcon} alt="Address" />
            <span className="color-text-secondary-dark">Address:</span>
          </Flex>
        </Col>
        <Col lg={18}>
          <Row gutter={[16]}>
            <Col lg={16}>
              <span>{address}</span>
              <h5 className="fw-500 mt-m mb-xs">Meeting instructions</h5>
              <p>{meetingInstructions}</p>
              <Button
                icon={
                  <Flex align="center">
                    <DirectionIcon />
                  </Flex>
                }
                color="primary"
                variant="outlined"
                size="large"
                onClick={handleGetDirections}
              >
                Get directions
              </Button>
            </Col>
            <Col lg={8}>
              {isLoaded && (
                <GoogleMap
                  mapContainerClassName="rounded"
                  options={{ ...MAP_OPTIONS, draggable: false }}
                  center={latLng}
                  zoom={zoom}
                  onLoad={onLoad}
                  onUnmount={onUnmount}
                >
                  <Circle
                    center={latLng}
                    radius={radiusInMeters}
                    options={{
                      strokeColor: themeColor,
                      strokeOpacity: 0.8,
                      strokeWeight: 2,
                      fillColor: themeColor,
                      fillOpacity: 0.12,
                      clickable: false
                    }}
                  />
                  <Marker
                    position={latLng}
                    icon={{ scaledSize: new window.google.maps.Size(30, 30) }}
                  />
                  <div className={styles.addressDropdownWrapper}>
                    <div className={styles.addressDropdown}>
                      <Icon
                        iconName="Location"
                        className={styles.locationIcon}
                        size={22}
                        style={{ color: "var(--color-primary)", marginRight: 8, verticalAlign: "middle" }}
                      />
                      {address}
                    </div>
                    <div className={styles.mapControls}>
                      <ZoomButtons />
                    </div>
                  </div>
                </GoogleMap>
              )}
            </Col>
          </Row>
        </Col>
      </Row>
      <Divider className="mb-l" />
      <Flex gap={8} align="center" justify="space-between">
        <div>
          <h6 className="fw-500 mb-s">Cancellation Policy</h6>
          <p className="mb-0">
            Free cancellation before 48 hours prior to trip due to our{' '}
            <a target="_blank" rel="noopener noreferrer" href="/#/cancellation-policy">
              Cancellation policy
            </a>
            .
          </p>
        </div>
        {/* <Button type="link" className="px-0" onClick={handleCancelClick}>
          Cancel Booking
        </Button> */}
      </Flex>
      {cancelModalOpen && (
        <CancelBookingModal
          open={cancelModalOpen}
          bookings={[cancelBookingData]}
          onConfirm={() => setCancelModalOpen(false)}
          onCancel={() => setCancelModalOpen(false)}
          reasons={reasons}
        />
      )}
    </Card>
  )
})

BookingDetailsCard.propTypes = {
  booking: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    uuid: PropTypes.string,
    booked_date: PropTypes.string,
    start_time: PropTypes.string,
    end_time: PropTypes.string,
    booking_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    create_at: PropTypes.string,
    trip_image: PropTypes.arrayOf(
      PropTypes.shape({
        placeholder_url: PropTypes.string
      })
    ),
    adult_count: PropTypes.number,
    child_count: PropTypes.number,
  }),
  trip: PropTypes.shape({
    address: PropTypes.string,
    available_hours_range: PropTypes.string,
    max_guests: PropTypes.number,
    location: PropTypes.shape({
      radius: PropTypes.number
    })
  }),
  meetingInstructions: PropTypes.string
}

BookingDetailsCard.displayName = "BookingDetailsCard"

export default BookingDetailsCard
